import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Check,
    CloudRain,
    Copy,
    Download,
    FileSpreadsheet,
    Maximize2,
    Minimize2,
    Printer,
    RefreshCw,
    Thermometer,
} from 'lucide-react';
import { copyTableElementToClipboard, pivotToTsv } from '../../utils/weatherExport';
import './weatherDetails.css';

/**
 * Tableau Date × Heure simple (sans heatmap) avec barre d'outils.
 */
const WeatherHeatmapTable = ({
    title,
    unitLabel,
    pivot,
    variant = 'precip', // precip | temp
    onRefresh,
    onExportCsv,
    onExportExcel,
    onPrint,
    refreshing = false,
}) => {
    const [copied, setCopied] = useState(false);
    const [fullscreen, setFullscreen] = useState(false);
    const cardRef = useRef(null);
    const tableRef = useRef(null);

    const Icon = variant === 'temp' ? Thermometer : CloudRain;
    const hasData = Boolean(pivot?.dates?.length && pivot?.rows?.length);

    const handleCopy = useCallback(async (e) => {
        e?.preventDefault?.();
        e?.stopPropagation?.();
        const tableEl = tableRef.current;
        const tsv = pivotToTsv(pivot);
        if (!tableEl || !tsv) return;
        const ok = await copyTableElementToClipboard(tableEl, tsv);
        if (ok) {
            setCopied(true);
            window.setTimeout(() => setCopied(false), 2000);
        }
    }, [pivot]);

    const toggleFullscreen = () => {
        setFullscreen((prev) => !prev);
    };

    useEffect(() => {
        if (!fullscreen) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') setFullscreen(false);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [fullscreen]);

    const cellDisplay = (cell) => (
        cell != null && typeof cell === 'object' ? (cell.display ?? '—') : (cell ?? '—')
    );

    return (
        <section
            ref={cardRef}
            className={`wd-card wd-slide-up ${fullscreen ? 'wd-fullscreen' : ''}`}
            aria-label={title}
        >
            <div className="wd-card__header">
                <h3 className="wd-card__title">
                    <Icon size={18} strokeWidth={2.25} aria-hidden="true" />
                    {title}
                    <span className="wd-card__title-meta">({unitLabel})</span>
                </h3>
                <div className="wd-toolbar wd-no-print" role="toolbar" aria-label={`Actions ${title}`}>
                    <button
                        type="button"
                        className="wd-btn wd-btn--ghost wd-btn--sm"
                        onClick={onRefresh}
                        disabled={refreshing}
                        title="Refresh"
                        aria-label="Refresh"
                    >
                        <RefreshCw size={14} className={refreshing ? 'wd-spin' : ''} aria-hidden="true" />
                        Refresh
                    </button>
                    <button
                        type="button"
                        className="wd-btn wd-btn--ghost wd-btn--sm"
                        onClick={onExportCsv}
                        disabled={!hasData}
                        title="Export CSV"
                        aria-label="Export CSV"
                    >
                        <Download size={14} aria-hidden="true" />
                        CSV
                    </button>
                    <button
                        type="button"
                        className="wd-btn wd-btn--ghost wd-btn--sm"
                        onClick={onExportExcel}
                        disabled={!hasData}
                        title="Export Excel"
                        aria-label="Export Excel"
                    >
                        <FileSpreadsheet size={14} aria-hidden="true" />
                        Excel
                    </button>
                    <button
                        type="button"
                        className="wd-btn wd-btn--ghost wd-btn--sm"
                        onClick={onPrint}
                        disabled={!hasData}
                        title="Print PDF"
                        aria-label="Print PDF"
                    >
                        <Printer size={14} aria-hidden="true" />
                        PDF
                    </button>
                    <button
                        type="button"
                        className="wd-btn wd-btn--ghost wd-btn--sm"
                        onClick={handleCopy}
                        disabled={!hasData}
                        title="Copier le tableau"
                        aria-label="Copier le tableau"
                    >
                        {copied ? <Check size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}
                        {copied ? 'Copié!' : 'Copier'}
                    </button>
                    <button
                        type="button"
                        className="wd-btn wd-btn--ghost wd-btn--sm"
                        onClick={toggleFullscreen}
                        title={fullscreen ? 'Quitter le plein écran' : 'Fullscreen'}
                        aria-label={fullscreen ? 'Quitter le plein écran' : 'Fullscreen'}
                        aria-pressed={fullscreen}
                    >
                        {fullscreen
                            ? <Minimize2 size={14} aria-hidden="true" />
                            : <Maximize2 size={14} aria-hidden="true" />}
                        {fullscreen ? 'Exit' : 'Fullscreen'}
                    </button>
                </div>
            </div>

            {!hasData ? (
                <div className="wd-table-empty">Aucune donnée {unitLabel} disponible pour ce séjour.</div>
            ) : (
                <div className="wd-card__body wd-card__body--flush">
                    <div
                        className="wd-table-wrap"
                        tabIndex={0}
                        role="region"
                        aria-label={`Tableau ${title}`}
                    >
                        <table className="wd-table" ref={tableRef}>
                            <thead>
                                <tr>
                                    <th className="wd-sticky-col" scope="col">Heure</th>
                                    {pivot.dates.map((date) => (
                                        <th key={date} scope="col">{date}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {pivot.rows.map((row) => (
                                    <tr key={row.hour}>
                                        <th className="wd-sticky-col" scope="row">{row.hour}</th>
                                        {row.cells.map((cell, idx) => {
                                            const date = cell.date || pivot.dates[idx];
                                            const display = cellDisplay(cell);
                                            const tip = `${date} · ${row.hour} · ${display}${unitLabel ? ` ${unitLabel}` : ''}`;
                                            return (
                                                <td
                                                    key={`${row.hour}-${date}`}
                                                    title={tip}
                                                    aria-label={tip}
                                                >
                                                    {display}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </section>
    );
};

export const WeatherPrecipitationTable = (props) => (
    <WeatherHeatmapTable
        {...props}
        title={props.title || 'Hourly Precipitation'}
        unitLabel={props.unitLabel || 'mm'}
        variant="precip"
    />
);

export const WeatherTemperatureTable = (props) => (
    <WeatherHeatmapTable
        {...props}
        title={props.title || 'Hourly Temperature'}
        unitLabel={props.unitLabel || '°C'}
        variant="temp"
    />
);

export default WeatherHeatmapTable;
